import React, { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { useQuery } from 'react-query';
import { AlertCircle, CheckCircle2, Circle, FileSignature } from 'lucide-react';
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';

import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiFetchResponse } from '../lib/apiFetch';
import { Funeral } from '../types';
import BenefitFormReviewModal, { BenefitFormReviewConfig } from './BenefitFormReviewModal';
import { ClaimAction, ClaimState, StepState, claimStatusTone, claimStatusWord, claimSteps } from './preneedClaim';

const PILL: Record<StepState, string> = {
  done: 'bg-emerald-100 text-emerald-800',
  now: 'bg-sky-100 text-sky-800',
  wait: 'bg-amber-100 text-amber-800',
  pending: 'bg-gray-100 text-gray-500',
};

function StepIcon({ state }: { state: StepState }) {
  if (state === 'done') return <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-hidden="true" />;
  if (state === 'wait') return <AlertCircle className="h-5 w-5 text-amber-600" aria-hidden="true" />;
  return <Circle className={cn('h-5 w-5', state === 'now' ? 'text-sky-600' : 'text-gray-300')} aria-hidden="true" />;
}

/** POST to the claims API; throws the server's own message. */
async function post(path: string, body: unknown = {}): Promise<any> {
  const res = await apiFetchResponse(`preneed-claims/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.message || `Request failed (${res.status})`);
  return json;
}

export function usePreneedClaim(funeralId: number | undefined, enabled: boolean) {
  return useQuery<ClaimState>(
    ['preneed-claim', funeralId],
    async () => {
      const res = await apiFetchResponse(`preneed-claims/${funeralId}`);
      if (!res.ok) throw new Error(`Claim state failed (${res.status})`);
      return res.json();
    },
    { enabled: !!funeralId && enabled, staleTime: 30_000 },
  );
}

const ACTION_LABEL: Record<ClaimAction, string> = {
  review: 'Review claim form',
  resend: 'Resend to family',
  send: 'Send to TruStage',
  mark_paid: 'Mark paid',
};

/**
 * « TruStage claim » (T-174), in the Details tab of a converted pre-need case
 * (after fruition; pre-plan only cases never have one). Review → Approve
 * stamps Bram's or Megan's stored signature into Section B; Québec then goes
 * to the family for Section C; the package goes to TruStage after cremation.
 */
export function TrustageClaimSection() {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const show = !!record?.has_preneed && !!record?.has_atneed;
  const funeralId = record ? Number(record.id) : undefined;
  const { data: state, refetch } = usePreneedClaim(funeralId, show);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [paidOpen, setPaidOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  if (!record || !show || !state?.claimable) return null;

  const steps = claimSteps(state);
  const tone = claimStatusTone(state);

  const act = async (key: string, path: string, body: unknown, done: string) => {
    setBusy(key);
    try {
      await post(`${funeralId}/${path}`, body);
      notify(done, { type: 'success' });
      void refetch();
      refresh();
      return true;
    } catch (err: any) {
      notify(err?.message || 'Something went wrong', { type: 'error' });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const run = (a: ClaimAction) => {
    if (a === 'review') return setReviewOpen(true);
    if (a === 'mark_paid') return setPaidOpen(true);
    if (a === 'resend') return void act(a, 'resend-signature', {}, 'Claim form sent again to the family');
    if (a === 'send') return void act(a, 'send', {}, 'Claim sent to TruStage');
  };

  const reviewConfig: BenefitFormReviewConfig = {
    title: 'TruStage claim form',
    pdfPath: 'preneed-claims/form',
    formKey: 'trustage_claim',
    saveFlattened: false,
    downloadName: `TruStage_Claim_${record.id}.pdf`,
  };

  return (
    <AccordionItem value="trustage-claim">
      <AccordionTrigger icon={<FileSignature className="w-4 h-4" />}>
        TruStage claim
        <span
          className={cn(
            'ml-2 rounded-md px-2 py-0.5 text-[11px] font-medium',
            tone === 'ok' ? 'bg-emerald-100 text-emerald-800' : tone === 'info' ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800',
          )}
        >
          {claimStatusWord(state)}
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="pt-1" data-testid="trustage-claim">
          {steps.map((s) => (
            <div key={s.key}>
              <div className="flex justify-between items-center gap-2 py-1.5">
                <span className="text-[13px] text-foreground">{s.label}</span>
                <span className="inline-flex items-center gap-1.5">
                  <StepIcon state={s.state} />
                  <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded tabular-nums', PILL[s.state])}>{s.pill}</span>
                </span>
              </div>
              {(s.note || s.actions?.length) && (
                <div className="mb-1.5 rounded-md border border-border bg-muted/50 px-2.5 py-2 text-[12px] leading-snug text-foreground">
                  {s.note && <div>{s.note}</div>}
                  {!!s.actions?.length && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {s.actions.map((a) => (
                        <Button key={a} size="sm" className="h-7 px-2.5 text-xs" disabled={!!busy} onClick={() => run(a)}>
                          {busy === a ? 'Working…' : ACTION_LABEL[a]}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <BenefitFormReviewModal
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          funeralId={Number(record.id)}
          config={reviewConfig}
          sent={!!state.claim?.approved_at}
          sendLabel={state.claim?.approved_at ? 'Approve again' : 'Approve…'}
          onSend={state.enabled ? () => setApproveOpen(true) : undefined}
        />

        <ApproveDialog
          open={approveOpen}
          state={state}
          busy={busy === 'approve'}
          onClose={() => setApproveOpen(false)}
          onApprove={async (body) => {
            const ok = await act(
              'approve',
              'approve',
              body,
              state.region === 'ontario' ? 'Claim approved' : 'Claim approved and sent to the family for signature',
            );
            if (ok) {
              setApproveOpen(false);
              setReviewOpen(false);
            }
          }}
        />

        <MarkPaidDialog
          open={paidOpen}
          busy={busy === 'mark_paid'}
          onClose={() => setPaidOpen(false)}
          onSave={async (body) => {
            if (await act('mark_paid', 'mark-paid', body, 'Claim marked paid')) setPaidOpen(false);
          }}
        />
      </AccordionContent>
    </AccordionItem>
  );
}

function ApproveDialog({
  open,
  state,
  busy,
  onClose,
  onApprove,
}: {
  open: boolean;
  state: ClaimState;
  busy: boolean;
  onClose: () => void;
  onApprove: (body: { cleoSigner: 'bram' | 'megan'; signerRole?: string; signerEmail?: string }) => void;
}) {
  const quebec = state.region !== 'ontario';
  const [cleoSigner, setCleoSigner] = useState<'bram' | 'megan'>(state.claim?.cleo_signer ?? state.defaultCleoSigner);
  const [role, setRole] = useState('executor');
  const [email, setEmail] = useState(state.signer?.email ?? '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Approve the claim form</DialogTitle>
      <DialogContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The stored signature is added to Section B with today&apos;s date, and your name is logged as the approver.
        </p>
        <div>
          <div className="text-[13px] font-medium mb-1">Section B signature</div>
          <RadioGroup row value={cleoSigner} onChange={(e) => setCleoSigner(e.target.value as 'bram' | 'megan')}>
            <FormControlLabel value="bram" control={<Radio size="small" />} label="Bram" />
            <FormControlLabel value="megan" control={<Radio size="small" />} label="Megan" />
          </RadioGroup>
        </div>
        {quebec && (
          <>
            <div className="text-[13px]">
              <span className="font-medium">Section C signer: </span>
              {state.signer?.name || 'no authorized representative on the case'}
            </div>
            <TextField select size="small" fullWidth label="Signs as" value={role} onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="executor">Executor</MenuItem>
              <MenuItem value="personal_representative">Personal representative</MenuItem>
              <MenuItem value="beneficiary">Beneficiary</MenuItem>
            </TextField>
            <TextField size="small" fullWidth label="Signer's email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton
          variant="contained"
          disabled={busy || (quebec && (!state.signer || !email.trim()))}
          onClick={() => onApprove({ cleoSigner, ...(quebec ? { signerRole: role, signerEmail: email.trim() } : {}) })}
        >
          {busy ? 'Approving…' : quebec ? 'Approve and send to family' : 'Approve'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}

function MarkPaidDialog({
  open,
  busy,
  onClose,
  onSave,
}: {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onSave: (body: { amount: number; date: string }) => void;
}) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const value = Number(amount.replace(/[$,\s]/g, ''));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>TruStage claim paid</DialogTitle>
      <DialogContent className="space-y-4">
        <p className="text-sm text-muted-foreground">From the TruStage deposit (« TruStageLOC Misc Payment »).</p>
        <TextField size="small" fullWidth label="Amount paid" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2,428.11" />
        <TextField size="small" fullWidth type="date" label="Deposit date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Cancel</MuiButton>
        <MuiButton variant="contained" disabled={busy || !(value > 0) || !date} onClick={() => onSave({ amount: value, date })}>
          {busy ? 'Saving…' : 'Mark paid'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}
